import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import { guaranteesApplicationStatusConfig } from '~/modules/guarantees/form/client/constants.ts';
import type {
  TGuaranteesAccount,
  TGuaranteesApplication,
  TGuaranteesBeneficiary,
} from '~/store/guarantees/types.ts';
import { GuaranteeSecurityType } from '~/store/guarantees/types.ts';
import {
  getGuaranteesApplicationDisplayNumber,
  normalizeGuaranteesApplicationStatus,
} from '~/store/guarantees/utils.ts';
import {
  getGuaranteesRootSavedOperationType,
  getGuaranteesRootSectionInitialValues,
  getGuaranteesStoredAccountSelection,
  getGuaranteesStoredContractSelection,
  guaranteesRootSectionResolver,
  shouldShowGuaranteesDependentSections,
} from '../../rootSection/utils.ts';
import type { TGuaranteesRootSectionFormValues } from '../../rootSection/types.ts';
import { hydrateGuaranteesApplicationForm } from '../utils/applicationHydration.ts';
import {
  getApplicationSavedSecurityAccountIban,
  getApplicationSecurityType,
  getGuaranteesApplicationIdentity,
} from '../utils/applicationIdentity.ts';

type TUseApplicationFormBootstrapParams = {
  accounts: TGuaranteesAccount[];
  application: TGuaranteesApplication | null;
  applicationId: string;
  beneficiaries: TGuaranteesBeneficiary[];
  guaranteeCurrencyOptions: TAutocompleteOption[];
  principalDebitAccounts: TGuaranteesAccount[];
  securityCurrencies: string[];
  isSecretSavedForCurrentApplication: boolean;
  routeContragentId: string;
  onApplicationChange: () => void;
};

export const useApplicationFormBootstrap = ({
  accounts,
  application,
  applicationId,
  beneficiaries,
  guaranteeCurrencyOptions,
  principalDebitAccounts,
  securityCurrencies,
  isSecretSavedForCurrentApplication,
  routeContragentId,
  onApplicationChange,
}: TUseApplicationFormBootstrapParams) => {
  const loadedApplicationId = getGuaranteesApplicationIdentity(application);
  const isNewApplicationRoute = applicationId === 'new';
  const previousRouteApplicationIdRef = React.useRef(applicationId);
  const initialNewRouteLoadedApplicationIdRef = React.useRef(
    isNewApplicationRoute ? loadedApplicationId : ''
  );

  if (previousRouteApplicationIdRef.current !== applicationId) {
    previousRouteApplicationIdRef.current = applicationId;
    initialNewRouteLoadedApplicationIdRef.current = isNewApplicationRoute
      ? loadedApplicationId
      : '';
  }

  const isApplicationCreatedFromNewRoute =
    isNewApplicationRoute &&
    !!loadedApplicationId &&
    loadedApplicationId !== initialNewRouteLoadedApplicationIdRef.current;
  const isLoadedApplicationCurrent =
    loadedApplicationId === applicationId || isApplicationCreatedFromNewRoute;
  const currentRouteApplication = isLoadedApplicationCurrent ? application : null;
  const contragentId =
    routeContragentId || String(currentRouteApplication?.contragentId ?? '').trim();
  const effectiveApplicationId =
    currentRouteApplication?.applicationId || currentRouteApplication?.id || applicationId;
  const storedAccountSelection = useMemo(
    () => getGuaranteesStoredAccountSelection(effectiveApplicationId),
    [effectiveApplicationId]
  );
  const storedContractSelection = useMemo(
    () => getGuaranteesStoredContractSelection(effectiveApplicationId),
    [effectiveApplicationId]
  );
  const formDefaultValues = useMemo(
    () =>
      getGuaranteesRootSectionInitialValues(
        currentRouteApplication,
        accounts,
        principalDebitAccounts,
        storedAccountSelection,
        storedContractSelection,
        guaranteeCurrencyOptions,
        securityCurrencies
      ),
    [
      accounts,
      currentRouteApplication,
      guaranteeCurrencyOptions,
      principalDebitAccounts,
      securityCurrencies,
      storedAccountSelection,
      storedContractSelection,
    ]
  );
  const savedGuaranteeOperationType = useMemo(
    () => getGuaranteesRootSavedOperationType(currentRouteApplication),
    [currentRouteApplication]
  );
  const formMethods = useForm<TGuaranteesRootSectionFormValues>({
    defaultValues: formDefaultValues,
    resolver: guaranteesRootSectionResolver,
    mode: 'onSubmit',
  });
  const { getFieldState, getValues, reset } = formMethods;
  const previousApplicationIdRef = React.useRef(effectiveApplicationId);
  const shouldSkipNextAccountRestrictionCheckRef = React.useRef(false);
  const {
    applicationNumber,
    isTemporaryApplicationNumber,
  } = getGuaranteesApplicationDisplayNumber(currentRouteApplication);
  const normalizedApplicationStatus = normalizeGuaranteesApplicationStatus(
    currentRouteApplication?.applicationStatus
  );
  const applicationStatusConfig = guaranteesApplicationStatusConfig[normalizedApplicationStatus];
  const primaryBeneficiary = beneficiaries[0] ?? null;
  const currentApplicationVersion = currentRouteApplication?.version ?? 0;
  const hasApplicationSavedSecurityAccount =
    !!getApplicationSavedSecurityAccountIban(currentRouteApplication, storedAccountSelection) ||
    getApplicationSecurityType(currentRouteApplication?.security) ===
      GuaranteeSecurityType.NO_SECURITY;
  const shouldShowDependentSections = shouldShowGuaranteesDependentSections({
    hasSavedSecurityAccount: hasApplicationSavedSecurityAccount,
    isSecretSavedForCurrentApplication,
  });

  React.useEffect(() => {
    hydrateGuaranteesApplicationForm({
      currentApplicationId: effectiveApplicationId,
      defaultValues: formDefaultValues,
      getFieldState,
      getValues,
      previousApplicationIdRef,
      reset,
      onApplicationChange: () => {
        onApplicationChange();
        shouldSkipNextAccountRestrictionCheckRef.current = false;
      },
    });
  }, [
    effectiveApplicationId,
    formDefaultValues,
    getFieldState,
    getValues,
    onApplicationChange,
    reset,
  ]);

  return {
    applicationNumber,
    isTemporaryApplicationNumber,
    applicationStatusConfig,
    contragentId,
    currentApplicationVersion,
    currentRouteApplication,
    effectiveApplicationId,
    formDefaultValues,
    formMethods,
    primaryBeneficiary,
    savedGuaranteeOperationType,
    shouldShowDependentSections,
    shouldSkipNextAccountRestrictionCheckRef,
    storedAccountSelection,
    storedContractSelection,
  };
};
